// Thin wrapper over the HustleShield Forms API. All calls use the
// shared axios instance (cookie-based auth) for the owner routes and
// `fetch` (no cookies) for the public, slug-addressable routes.
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
// Public submissions on other module hosts still use Shield's protected ingress.
export const PUBLIC_FORMS_BACKEND = BACKEND || (
  typeof window!=="undefined" && !["app.hustleshield.au","hustleshield.au","www.hustleshield.au","emailspamshield.com","www.emailspamshield.com","app.hustlesuite.au","hustlesuite.au","www.hustlesuite.au"].includes(window.location.hostname)
  ? "https://app.hustleshield.au" : "");


export const formsApi = {
  reviews:async id=>(await api.get("/forms/"+id+"/reviews")).data,
  moderateReview:async(id,rid,status)=>(await api.patch("/forms/"+id+"/reviews/"+rid,{status})).data,
  paymentConnections:async id=>(await api.get("/forms/"+id+"/payment-connections")).data,
  savePaymentConnection:async(id,data)=>(await api.post("/forms/"+id+"/payment-connections",data)).data,
  customiseDirectory: async id => (await api.post("/forms/directory/"+encodeURIComponent(id)+"/customise")).data,
  templates: async () => (await api.get("/forms/templates",{params:{include_schemas:true}})).data,
  list: async () => (await api.get("/forms")).data,
  ghlConnections:async id=>(await api.get("/forms/"+id+"/ghl/connections")).data,
  ghlConnect:async(id,data)=>(await api.post("/forms/"+id+"/ghl/connections",data)).data,
  ghlFields:async(id,connection)=>(await api.get("/forms/"+id+"/ghl/fields",{params:{connection_id:connection}})).data,
  ghlRetry:async(id,submission)=>(await api.post("/forms/"+id+"/submissions/"+submission+"/ghl-retry")).data,

  get: async (id) => (await api.get(`/forms/${id}`)).data,
  create: async (payload) => (await api.post("/forms", payload)).data,
  importPortable: async raw => {
    const data=readPortableForm(raw);
    return (await api.post("/forms",{name:((data.name || "Imported form")+" — copy").slice(0,120),template:data})).data;
  },
  update: async (id, payload) =>
    (await api.put(`/forms/${id}`, payload)).data,
  remove: async (id) => (await api.delete(`/forms/${id}`)).data,
  duplicate: async (id) => (await api.post(`/forms/${id}/duplicate`)).data,
  publish: async (id, publish = true) =>
    (await api.post(`/forms/${id}/publish`, { publish })).data,
  submissions: async (id, { skip = 0, limit = 100 } = {}) =>
    (await api.get(`/forms/${id}/submissions?skip=${skip}&limit=${limit}`)).data,
  csvUrl: (id) => `${BACKEND}/api/forms/${id}/submissions.csv`,
  aiGenerate: async (prompt, form_id = null) =>
    (await api.post("/forms/ai/generate", { prompt, form_id, request_id: crypto.randomUUID() })).data,
};

export const publicApi = {
  get: async (slug) => {
    const r = await fetch(`${PUBLIC_FORMS_BACKEND}/api/forms/public/${slug}`, {
      credentials: "omit",
    });
    if (!r.ok) throw new Error(`${r.status}`);
    return r.json();
  },
  slots: async (slug, startDate, endDate, fieldId) => {
    const q = new URLSearchParams({ start_date: startDate });
    if (endDate) q.set("end_date", endDate);
    if (fieldId) q.set("field_id", fieldId);
    const r = await fetch(
      `${PUBLIC_FORMS_BACKEND}/api/forms/public/${slug}/appointment/slots?${q}`,
      { credentials: "omit" },
    );
    if (!r.ok) throw new Error(`${r.status}`);
    return r.json();
  },
  icsUrl: (slug, subId) =>
    `${PUBLIC_FORMS_BACKEND}/api/forms/public/${slug}/appointment/ics?sub_id=${encodeURIComponent(subId)}`,
};

// Return a safe public preview-shape of an owner-side form document, so
// the live preview inside the builder renders exactly like /f/{slug}.
export function toPublicPreview(form) {
  const s = form?.schema || {};
  const integ = s.integrations || {};
  const pp = integ.paypal || {};
  const pub_fields = (s.fields || []).map((f) => {
    if (f.type !== "appointment") return f;
    const cfg = f.appointment || {};
    return {
      ...f,
      appointment: {
        timezone: cfg.timezone || "Australia/Sydney",
        slot_minutes: Number(cfg.slot_minutes || 30),
        booking_window_days: Number(cfg.booking_window_days || 30),
        days: cfg.days || ["mon","tue","wed","thu","fri"],
        start_time: cfg.start_time || "09:00",
        end_time: cfg.end_time || "17:00",
        location: cfg.location || "",
        description: cfg.description || "",
      },
    };
  });
  return {
    id: form?.id,
    slug: form?.slug,
    name: form?.name,
    schema: {
      fields: pub_fields,
      steps: s.steps || [],
      form_type: s.form_type || "standard",
      settings: {
        ...Object.fromEntries(Object.entries(s.settings || {}).filter(([k])=>["show_business_name","show_title","show_subtitle","subtitle_position","show_footer","footer_text","header_logo","business_name","header_style","button_style","card_style","layout_gap"].includes(k))),
        submit_label: s.settings?.submit_label || "Submit",
        thankyou_message: s.settings?.thankyou_message || "Thanks!",
        thankyou_url: s.settings?.thankyou_url || "",
        enable_payment: !!s.settings?.enable_payment,
        subtitle: s.settings?.subtitle || "",
        custom_css: s.settings?.custom_css || "",
      },
      theme: s.theme || {},
      payment: s.payment || { currency: "AUD", mode: "fixed", amount: 0 },
      paypal_public: {
        client_id: pp.client_id || "",
        mode: pp.mode || "sandbox",
        enabled: !!pp.client_id,
      },
    },
    ts_token: "", turnstile_site_key: "",
  };
}

// Shared presentation and portability contract: the builder and public renderer use the same values.
export function presentationStyle(s = {}) {
  const out = {};
  const px = {font_size:"fontSize",padding:"padding",margin:"margin",gap:"gap",border_width:"borderWidth",border_radius:"borderRadius",image_width:"width",image_height:"height"};
  Object.entries(px).forEach(([key, css]) => {
    if (s[key] !== "" && s[key] != null && Number.isFinite(Number(s[key]))) out[css] = Math.max(0,Math.min(1600,Number(s[key])))+"px";
  });
  if(s.font_family) out.fontFamily=s.font_family;
  if(s.bold) out.fontWeight=700;
  if(s.italic) out.fontStyle="italic";
  if(s.underline) out.textDecoration="underline";
  if(s.color) out.color=s.color;
  if(s.background) out.backgroundColor=s.background;
  if(s.align) out.textAlign=s.align;
  if(s.border_color) out.borderColor=s.border_color;
  if(["solid","dashed","dotted","double","none"].includes(s.border_style)) out.borderStyle=s.border_style;
  return out;
}
export function portableForm(form, copyAll = false) {
  const schema=structuredClone(form.schema || {});
  const connectionNames=Object.keys(schema.integrations || {});
  schema.integrations={resend_notify:copyAll ? {...(schema.integrations?.resend_notify || {}),enabled:false} : {enabled:false,recipients:[]}};
  schema.settings={...(schema.settings || {}),notify_email:copyAll ? (schema.settings?.notify_email || "") : "",enable_payment:false};
  ["allowed_hosts","embed_hosts","allowed_domains"].forEach(k=>delete schema.settings[k]);
  return {format:"hustleshield-form",version:1,name:form.name,schema,copy_all_settings:copyAll,reconnect:connectionNames,
    note:copyAll ? "Includes design, logic, notification recipients/languages and confirmations. Delivery stays disabled until reviewed. Reconnect private integrations; credentials and submissions are never exported." : "Design and rules only. Reconnect recipients, payments and integrations. Images retain their public URLs."};
}
export function readPortableForm(raw) {
  if(!raw || raw.format!=="hustleshield-form" || raw.version!==1 || !Array.isArray(raw.schema?.fields)) throw Error("Choose a HustleShield version 1 form export.");
  if(JSON.stringify(raw).length>250000 || raw.schema.fields.length>300) throw Error("Form export is too large.");
  return portableForm(raw, raw.copy_all_settings === true);
}

// Explicit block margins work with the platform's image reset and in both renderers.
export function imagePresentationStyle(field = {}) {
  const align=field.style?.align || field.align || "left";
  return {...presentationStyle(field.style),maxWidth:"100%",borderRadius:field.style?.border_radius ?? 10,
    height:field.style?.image_height ? Number(field.style.image_height)+"px":"auto",objectFit:"contain",display:"block",marginLeft:align==="left"?0:"auto",marginRight:align==="right"?0:"auto"};
}
